import { useState } from "react";
import { ActionModal } from "@/app/ActionModal";
import { EntityDrawer } from "@/app/EntityDrawer";
import { TextField } from "@/app/fields";
import { priceTimeline, type PriceSyncSetting } from "@/app/priceTimeline";
import { Alert, Button, formatTime, Form } from "@/ui";
import { priceSyncRateDraft, priceSyncRateInput, rateSummary } from "./priceSyncRate";

const INVALIDATE = ["price-sync-setting", "price-sync-tasks"];

/** 全局暂停横幅（D218）：写明谁、何时暂停，以及与“失败待恢复”的区别；队首仍显示“执行中”，所以在这里说明。 */
export function PausedBanner({ setting }: { setting: PriceSyncSetting }) {
  if (!setting.paused) return null;
  const who = [setting.paused_by, setting.paused_at && formatTime(setting.paused_at)].filter(Boolean).join("，");
  return (
    <Alert tone="warning" title={`价格同步已暂停${who && `（${who}）`}`}>
      新任务排队但不执行，追补在途照常检查；队首任务虽显示“执行中”，实际不推进，恢复后从断点继续。与“失败待恢复”不同：失败只挡住它后面的任务、要在任务行点“重试”；暂停是全局的，要点页头的“恢复”。
    </Alert>
  );
}

/** 暂停或恢复的二次确认：暂停写清影响（改价不生效、归档被拖住、超过 12 小时告警），错误留在弹窗。 */
export function PauseToggle({ paused }: { paused: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={paused ? "primary" : "danger"} onClick={() => setOpen(true)}>{paused ? "恢复同步" : "暂停同步"}</Button>
      {open && (paused
        ? <ActionModal title="恢复价格同步" confirmLabel="确认恢复" note="恢复后 cron 在下一秒从各任务的断点继续，结果与不暂停一致。操作进入审计并发事件通知。"
            action={priceTimeline.resume} invalidate={INVALIDATE} message="价格同步已恢复" onClose={() => setOpen(false)} />
        : <ActionModal title="暂停价格同步" confirmLabel="确认暂停" danger note="暂停后不再开新批，已开始的一批做完；所有任务（含升级回填）停在当前断点，新任务只排队。暂停期间改价不会生效，归档会被拖住，超过 12 小时会告警。追补在途的检查不受影响。操作进入审计并发事件通知。"
            action={priceTimeline.pause} invalidate={INVALIDATE} message="价格同步已暂停" onClose={() => setOpen(false)} />)}
    </>
  );
}

/** 限速参数：显示当前值，有写权限时可在抽屉里修改（D218）。 */
export function RateSetting({ setting, canWrite }: { setting: PriceSyncSetting; canWrite: boolean }) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <p className="ax-secondary">{rateSummary(setting)}{canWrite && <> <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>限速设置</Button></>}</p>
      {editing && (
        <EntityDrawer title="价格同步限速" initial={priceSyncRateDraft(setting)} convert={priceSyncRateInput} save={priceTimeline.saveSetting}
          invalidate={["price-sync-setting"]} message="限速参数已保存；调回不限速后，最多还要等一次已排定的间隔才生效" onClose={() => setEditing(false)}>
          {({ draft, errors, set }) => (
            <Form>
              <TextField id="ps-gap" label="最小间隔（毫秒）" required mono type="number" value={draft.min_gap_ms} error={errors.min_gap_ms}
                hint="每批结束后至少等这么久再开下一批，0 到 60000，默认 0" onChange={(v) => set("min_gap_ms", v)} />
              <TextField id="ps-duty" label="占空比（%）" required mono type="number" value={draft.duty_percent} error={errors.duty_percent}
                hint="1 到 100，默认 100。占空比 50%：每批跑多久就歇多久，总时长约翻倍。等待取两者较大值；最小间隔 0 且占空比 100 即不限速" onChange={(v) => set("duty_percent", v)} />
            </Form>
          )}
        </EntityDrawer>
      )}
    </>
  );
}
